import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { fantasyEndpoints } from '@pff-consumer/api-sdk'
import { HYDRATE } from 'next-redux-wrapper'
import { envConfig, getBaseUrl } from '@pff-consumer/utils'
import type { League, FantasyRoster, MatchupRecord, GameMatchup, WeeklyRankingsEntity } from '@pff-consumer/schema'
import { FantasyPosition } from '@pff-consumer/schema'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { getWeeklyRankings } from '../consumer-api/consumer-api'

const { CONSUMER_API_KEY } = envConfig

const getAuthTokenFromState = (state: unknown): string | undefined => {
  // @ts-ignore
  return state?.account?.token
}

const baseUrl = getBaseUrl()

export const fantasyInSeasonApi = createApi({
  reducerPath: 'fantasyInSeasonApi',
  tagTypes: ['RosterAndMatchupForInSeason'],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      // We need to get the auth/jwt token from the store and add to the headers for every api call
      const authToken = getAuthTokenFromState(getState())
      if (authToken) {
        headers.append('Authorization', authToken)
      }
      headers.append('Api-Key', CONSUMER_API_KEY)

      return headers
    },
  }),
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === HYDRATE) {
      return action.payload[reducerPath]
    }
    return undefined
  },
  endpoints: (builder) => ({
    getMyLeaguesForInSeason: builder.query<{ leagues: League[] }, { season: number }>({
      keepUnusedDataFor: 120,
      query: fantasyEndpoints.getMyLeagues,
      transformResponse: (response: { leagues: League[] }) => {
        return {
          leagues: response.leagues.sort((league1, league2) => {
            const league1TimeStamp = new Date(league1.created_at).getTime()
            const league2TimeStamp = new Date(league2.created_at).getTime()
            return league2TimeStamp - league1TimeStamp
          }),
        }
      },
    }),
    getMyTeamData: builder.query<
      { roster: FantasyRoster; gameWeek: number; matchup: GameMatchup; rankings: WeeklyRankingsEntity[] } & {
        authorizeUrl?: string
      },
      { leagueId: number }
    >({
      providesTags: ['RosterAndMatchupForInSeason'],
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const fantasyMatchup = await fetchWithBQ(fantasyEndpoints.inSeason.getMatchupRecord({ leagueId: arg.leagueId }))

        if (fantasyMatchup.error) {
          return { error: fantasyMatchup.error as FetchBaseQueryError }
        }

        const fantasyRoster = await fetchWithBQ(fantasyEndpoints.inSeason.getRoster({ leagueId: arg.leagueId }))
        if (fantasyRoster.error) {
          return { error: fantasyRoster.error as FetchBaseQueryError }
        }
        // ts-ignore is needed here as using type RootState here throws circular dependency error
        // @ts-ignore
        const state = _queryApi.getState()
        // @ts-ignore
        const filters = { ...state.fantasy.inSeason.weeklyRankings.filters, position: FantasyPosition.FLEX }

        const weeklyRankings = await _queryApi.dispatch(getWeeklyRankings.initiate(filters))
        if (weeklyRankings.error) {
          return { error: weeklyRankings.error as FetchBaseQueryError }
        }

        const rosterData: { roster: FantasyRoster } = fantasyRoster.data as { roster: FantasyRoster }
        const matchupData: MatchupRecord = fantasyMatchup.data as MatchupRecord
        const rankings: WeeklyRankingsEntity[] = weeklyRankings?.data?.rankings as WeeklyRankingsEntity[]

        const stitchedData = {
          ...rosterData,
          ...matchupData,
          rankings,
        }
        return { data: stitchedData }
      },
    }),
    getRosterForInSeason: builder.query<{ roster: FantasyRoster }, { leagueId: number }>({
      keepUnusedDataFor: 5,
      query: fantasyEndpoints.inSeason.getRoster,
    }),
    getMatchupRecord: builder.query<MatchupRecord, { leagueId: number }>({
      keepUnusedDataFor: 5,
      query: fantasyEndpoints.inSeason.getMatchupRecord,
    }),
    resyncSeasonFantasyLeague: builder.mutation<{ league: League }, { leagueId: string }>({
      // We need to invalidate getMyTeamData because we are using this mutation to resync the user's
      // league in the case that their team name has changed since the last time they synced. Once resynced
      // the roster should automatically resolve or uncover a new error with the user's roster.
      invalidatesTags: ['RosterAndMatchupForInSeason'],
      query: ({ leagueId }) => ({
        url: `${fantasyEndpoints.resyncFantasyLeague(leagueId)}`,
        method: 'POST',
        body: {},
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMyLeaguesForInSeasonQuery,
  useGetRosterForInSeasonQuery,
  useGetMatchupRecordQuery,
  useGetMyTeamDataQuery,
  useResyncSeasonFantasyLeagueMutation,
} = fantasyInSeasonApi

// Export getters to be used when rendering components on the server
export const { getMyLeaguesForInSeason, getMatchupRecord, getMyTeamData } = fantasyInSeasonApi.endpoints
